import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocale } from '../../app/locale/LocaleProvider';
import type { Locale } from '../../app/locale/resolveLocale';
import type { CoreEvent, DimensionDeltas } from '../../data/choiceMaps';
import { scenes, type Scene } from '../../data/scenes';
import { matchPersonalityType } from '../../engine/resultMatcher';
import { scoreEvents, type ScoreEventInput } from '../../engine/scoring';
import { parseChallengePayload } from '../../engine/sharePayload';
import { GestureSurface, type GestureTelemetry } from './GestureSurface';

type SceneFollowUpChoice = {
  scoreChoiceId: string;
  label: Record<Locale, string>;
};

const sceneFollowUps: Record<string, Partial<Record<CoreEvent, SceneFollowUpChoice[]>>> = {
  friend_last_minute_cancel: {
    swipe_up_escape: [
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '先不回', en: 'Pause reply' } },
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '晚点说', en: 'Later' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '装没看', en: 'Miss it' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '别画饼', en: 'No vague next' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '这次不行', en: 'Not okay' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '说清楚', en: 'Be clear' } }
    ],
    swipe_left_play_cool: [
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '转给朋友', en: 'Send friend' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '装没事', en: 'Act fine' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '冷笑下', en: 'Dry smile' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '改时间', en: 'Reschedule' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '留台阶', en: 'Leave ramp' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '问原因', en: 'Ask why' } }
    ],
    fast_fling_nope: [
      { scoreChoiceId: 'hard_exit', label: { 'zh-CN': '不约了', en: 'Cancel it' } },
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '先散场', en: 'Drop out' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '换个局', en: 'New plan' } }
    ]
  },
  group_credit_repeat: {
    long_press_suppress: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '先憋住', en: 'Hold it' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '记下来', en: 'Note it' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '暂不说', en: 'Stay out' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '这是我说的', en: 'My point' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '请标出处', en: 'Name source' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '别抢功', en: 'Credit me' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'organize_group', label: { 'zh-CN': '理脉络', en: 'Thread it' } },
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '补出处', en: 'Add source' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '标我名', en: 'Name me' } }
    ],
    swipe_left_play_cool: [
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '转给朋友', en: 'Send proof' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '表面嗯嗯', en: 'Smile along' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '轻轻刺', en: 'Dry jab' } }
    ]
  },
  serious_message_flat_reply: {
    timed_hesitation: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '先缓缓', en: 'Take a beat' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '看语气', en: 'Read tone' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '问一句', en: 'Ask once' } }
    ],
    swipe_up_escape: [
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '算了不说', en: 'Drop it' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '先撤了', en: 'Step out' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '晚点回', en: 'Reply later' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '补一句', en: 'Add one' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '问感受', en: 'Ask tone' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '给台阶', en: 'Offer ramp' } }
    ],
    double_tap_meme: [
      { scoreChoiceId: 'meme_reply', label: { 'zh-CN': '发个哈哈', en: 'Haha back' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '梗住了', en: 'Meme wall' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '表情糊过', en: 'Sticker it' } }
    ],
    long_press_suppress: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '打完删', en: 'Type delete' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '静音冷却', en: 'Mute cool' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '先收住', en: 'Hold it' } }
    ]
  },
  after_hours_work_ping: {
    swipe_up_escape: [
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '装没看', en: 'Miss it' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '明早回', en: 'Morning' } },
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '先下线', en: 'Go offline' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '现在不看', en: 'Not now' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '下班了', en: 'Off hours' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '别催我', en: 'No rush' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '明早看', en: 'Morning' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '给时限', en: 'Set time' } },
      { scoreChoiceId: 'organize_group', label: { 'zh-CN': '排优先', en: 'Prioritize' } }
    ],
    swipe_left_play_cool: [
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '装在忙', en: 'Act busy' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '转给朋友', en: 'Send rant' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '礼貌嗯', en: 'Polite ok' } }
    ],
    long_press_suppress: [
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '先静音', en: 'Mute it' } },
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '草稿删', en: 'Delete draft' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '明早排', en: 'Queue it' } }
    ]
  },
  boundary_discomfort: {
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '说感受', en: 'Name feel' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '划底线', en: 'Draw line' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '给说法', en: 'Ask reason' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '别否认', en: 'Do not deny' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '我不舒服', en: 'Not okay' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '停一下', en: 'Stop there' } }
    ],
    long_press_suppress: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '先忍住', en: 'Hold it' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '浅点头', en: 'Nod once' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '换口气', en: 'Soften' } }
    ],
    fast_fling_nope: [
      { scoreChoiceId: 'hard_exit', label: { 'zh-CN': '我先走', en: 'Step out' } },
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '不聊了', en: 'End chat' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '换话题', en: 'Switch topic' } }
    ]
  },
  social_comment_side_eye: {
    double_tap_meme: [
      { scoreChoiceId: 'meme_reply', label: { 'zh-CN': '回个梗', en: 'Meme back' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '求放过', en: 'Spare me' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '贴表情', en: 'Sticker it' } }
    ],
    swipe_left_play_cool: [
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '装没事', en: 'Act fine' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '转给朋友', en: 'Send rant' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '礼貌阴阳', en: 'Dry jab' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '私聊问', en: 'DM ask' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '解释下', en: 'Clarify' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '留台阶', en: 'Offer ramp' } }
    ],
    swipe_up_escape: [
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '不搭理', en: 'Ignore' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '先隐身', en: 'Hide' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '晚点回', en: 'Later' } }
    ],
    shake_spiral: [
      { scoreChoiceId: 'plot_twist', label: { 'zh-CN': '气笑了', en: 'Angry laugh' } },
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '打满屏', en: 'Wall text' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '反手整', en: 'Flip it' } }
    ]
  },
  public_queue_cut: {
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '请排队', en: 'Line up' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '我在前', en: 'I was here' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '别插队', en: 'No cutting' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '提醒顺序', en: 'Note order' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '好好说', en: 'Say calmly' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '请回后面', en: 'Back please' } }
    ],
    long_press_suppress: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '忍一下', en: 'Hold it' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '看别处', en: 'Look away' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '换口气', en: 'Soften' } }
    ],
    fast_fling_nope: [
      { scoreChoiceId: 'hard_exit', label: { 'zh-CN': '换队伍', en: 'New line' } },
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '直接走', en: 'Walk off' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '不耗了', en: 'Not worth' } }
    ]
  },
  just_be_direct_claim: {
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '说边界', en: 'Name line' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '请换说法', en: 'Rephrase' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '给台阶', en: 'Offer ramp' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '别拿直当刀', en: 'Not honesty' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '这话伤人', en: 'That hurts' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '别继续', en: 'Stop now' } }
    ],
    swipe_left_play_cool: [
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '装没事', en: 'Act fine' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '笑着过', en: 'Smile back' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '心里吐槽', en: 'Inner rant' } }
    ],
    shake_spiral: [
      { scoreChoiceId: 'plot_twist', label: { 'zh-CN': '气上来', en: 'Heat up' } },
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '脑内长篇', en: 'Mind essay' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '冷场了', en: 'Freeze room' } }
    ]
  },
  group_chat_heating_up: {
    long_press_suppress: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '先憋住', en: 'Hold it' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '静音会儿', en: 'Mute bit' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '暂不插', en: 'Stay out' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'organize_group', label: { 'zh-CN': '拉回题', en: 'Back topic' } },
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '定结论', en: 'Pick answer' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '排优先', en: 'Sort it' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '我来说', en: 'I will say' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '答案在这', en: 'Here it is' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '别吵了', en: 'Stop noise' } }
    ],
    double_tap_meme: [
      { scoreChoiceId: 'meme_reply', label: { 'zh-CN': '发个梗', en: 'Send meme' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '降降温', en: 'Cool room' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '贴表情', en: 'Sticker' } }
    ],
    shake_spiral: [
      { scoreChoiceId: 'plot_twist', label: { 'zh-CN': '上头了', en: 'Heat up' } },
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '加把火', en: 'Add fuel' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '打满屏', en: 'Wall text' } }
    ]
  },
  read_no_reply_social_proof: {
    timed_hesitation: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '再等等', en: 'Wait it out' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '看动态', en: 'Check posts' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '去做事', en: 'Move on' } }
    ],
    swipe_up_escape: [
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '我也撤', en: 'I leave' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '先晾着', en: 'Let sit' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '不看了', en: 'Stop look' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '回我一下', en: 'Reply me' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '别装没看', en: 'Do not dodge' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '给个准话', en: 'Clear answer' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '问一句', en: 'Ask once' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '留余地', en: 'Leave room' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '给台阶', en: 'Offer ramp' } }
    ],
    double_tap_meme: [
      { scoreChoiceId: 'meme_reply', label: { 'zh-CN': '发个梗', en: 'Send meme' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '表情试探', en: 'Sticker test' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '截图笑', en: 'Screenshot' } }
    ]
  },
  meeting_silence_opinion: {
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '我不同意', en: 'Disagree' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '说个点', en: 'One point' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '别跳过', en: 'Do not skip' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '补数据', en: 'Add data' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '换说法', en: 'Rephrase' } },
      { scoreChoiceId: 'organize_group', label: { 'zh-CN': '拉回议题', en: 'Back topic' } }
    ],
    long_press_suppress: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '先不说', en: 'Not now' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '看材料', en: 'Read notes' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '会后讲', en: 'After meet' } }
    ],
    swipe_left_play_cool: [
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '装镇定', en: 'Look calm' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '记心里', en: 'Note inside' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '笑着听', en: 'Smile listen' } }
    ]
  },
  topic_takeover: {
    fast_fling_nope: [
      { scoreChoiceId: 'hard_exit', label: { 'zh-CN': '我先走', en: 'Step out' } },
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '不开了', en: 'Drop it' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '换桌聊', en: 'New table' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'organize_group', label: { 'zh-CN': '拉回我话', en: 'Back to me' } },
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '重开话题', en: 'Restart' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '请听完', en: 'Hear me' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '听我说完', en: 'Let me finish' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '别抢话题', en: 'No takeover' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '先停一下', en: 'Pause there' } }
    ],
    swipe_left_play_cool: [
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '装没事', en: 'Act fine' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '心里吐槽', en: 'Inner rant' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '笑着等', en: 'Wait smiling' } }
    ]
  },
  small_task_expands: {
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '先拆清', en: 'Scope it' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '给时限', en: 'Set time' } },
      { scoreChoiceId: 'organize_group', label: { 'zh-CN': '说范围', en: 'Set scope' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '半天不行', en: 'Too much' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '讲清成本', en: 'Name cost' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '别加码', en: 'No add-ons' } }
    ],
    swipe_up_escape: [
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '装没空', en: 'No time' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '晚点看', en: 'Later' } },
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '先躲开', en: 'Step away' } }
    ],
    long_press_suppress: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '先别答', en: 'Do not answer' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '算工时', en: 'Count hours' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '暂不接', en: 'Decline now' } }
    ]
  },
  wrong_order_pressure: {
    shake_spiral: [
      { scoreChoiceId: 'plot_twist', label: { 'zh-CN': '气到了', en: 'So mad' } },
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '脑内长评', en: 'Mind review' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '反手拍', en: 'Photo proof' } }
    ],
    fast_fling_nope: [
      { scoreChoiceId: 'hard_exit', label: { 'zh-CN': '直接退', en: 'Refund now' } },
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '不吃了', en: 'Skip meal' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '换一家', en: 'New place' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '找客服', en: 'Ask support' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '提补偿', en: 'Ask comp' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '重下单', en: 'Reorder' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '备注错了', en: 'Note missed' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '请重做', en: 'Redo please' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '给补偿', en: 'Comp it' } }
    ],
    swipe_left_play_cool: [
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '先笑笑', en: 'Laugh it' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '心里吐槽', en: 'Inner rant' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '装淡定', en: 'Act calm' } }
    ]
  },
  public_hot_take_cooldown: {
    long_press_suppress: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '先别转', en: 'Do not post' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '关页面', en: 'Close tab' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '深呼吸', en: 'Breathe' } }
    ],
    double_tap_meme: [
      { scoreChoiceId: 'meme_reply', label: { 'zh-CN': '发个梗', en: 'Send meme' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '梗一下', en: 'Meme jab' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '截图笑', en: 'Screenshot' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '我不同意', en: 'Disagree' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '这不对', en: 'Not right' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '别带节奏', en: 'No spin' } }
    ],
    swipe_up_escape: [
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '不参战', en: 'No fight' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '划走了', en: 'Swipe away' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '晚点看', en: 'Later' } }
    ],
    shake_spiral: [
      { scoreChoiceId: 'plot_twist', label: { 'zh-CN': '上头转', en: 'Repost hot' } },
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '打满屏', en: 'Wall text' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '开支线', en: 'Side quest' } }
    ]
  },
  borrowed_item_not_returned: {
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '问归还', en: 'Ask return' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '定日期', en: 'Set date' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '给提醒', en: 'Reminder' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '该还了', en: 'Return it' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '别拖了', en: 'No delay' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '说个日子', en: 'Pick day' } }
    ],
    long_press_suppress: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '忍一下', en: 'Hold it' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '先记着', en: 'Log it' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '晚点提', en: 'Ask later' } }
    ],
    swipe_up_escape: [
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '先不催', en: 'Do not ask' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '晚点问', en: 'Ask later' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '装忘了', en: 'Forget it' } }
    ]
  },
  vague_apology_non_apology: {
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '别来如果', en: 'No if' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '重说吧', en: 'Try again' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '别甩锅', en: 'Own it' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '说具体', en: 'Be specific' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '补个道歉', en: 'Real sorry' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '讲影响', en: 'Name impact' } }
    ],
    long_press_suppress: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '先忍住', en: 'Hold it' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '打完删', en: 'Type delete' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '缓缓气', en: 'Cool off' } }
    ],
    shake_spiral: [
      { scoreChoiceId: 'plot_twist', label: { 'zh-CN': '血压上来', en: 'Pressure up' } },
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '打满屏', en: 'Wall text' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '气笑了', en: 'Angry laugh' } }
    ]
  },
  friend_dumping_no_space: {
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '说精力', en: 'Name energy' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '定十分钟', en: 'Ten minutes' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '换个时间', en: 'New time' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '我没精力', en: 'No energy' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '先暂停', en: 'Pause it' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '晚点聊', en: 'Talk later' } }
    ],
    swipe_up_escape: [
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '去倒水', en: 'Get water' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '先离开', en: 'Step out' } },
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '晚点听', en: 'Later' } }
    ],
    long_press_suppress: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '先听完', en: 'Hear out' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '嗯嗯带过', en: 'Low reply' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '看时间', en: 'Check time' } }
    ],
    fast_fling_nope: [
      { scoreChoiceId: 'hard_exit', label: { 'zh-CN': '结束对话', en: 'End talk' } },
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '我先退', en: 'I am out' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '不聊了', en: 'No more' } }
    ]
  },
  group_plan_no_decider: {
    swipe_right_fix: [
      { scoreChoiceId: 'organize_group', label: { 'zh-CN': '定时间', en: 'Set time' } },
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '拉投票', en: 'Make poll' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '排方案', en: 'Sort plan' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '我来定', en: 'I choose' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '别空聊', en: 'No loop' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '现在选', en: 'Pick now' } }
    ],
    double_tap_meme: [
      { scoreChoiceId: 'meme_reply', label: { 'zh-CN': '发个投票', en: 'Poll meme' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '梗催一下', en: 'Meme nudge' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '表情催', en: 'Sticker nudge' } }
    ],
    swipe_left_play_cool: [
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '装轻松', en: 'Act chill' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '阴阳一句', en: 'Dry jab' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '转给朋友', en: 'Send rant' } }
    ]
  },
  message_misread_in_group: {
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '补解释', en: 'Clarify' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '换说法', en: 'Rephrase' } },
      { scoreChoiceId: 'organize_group', label: { 'zh-CN': '拉回原意', en: 'Back intent' } }
    ],
    double_tap_meme: [
      { scoreChoiceId: 'meme_reply', label: { 'zh-CN': '发个梗', en: 'Send meme' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '表情救场', en: 'Sticker save' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '截图自嘲', en: 'Self roast' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '我没阴阳', en: 'Not shade' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '别误读', en: 'Do not twist' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '听我说', en: 'Hear me' } }
    ],
    timed_hesitation: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '先别发', en: 'Hold draft' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '看上下文', en: 'Read the room' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '改白话', en: 'Plain English' } }
    ]
  },
  bill_split_unfair: {
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '不该平摊', en: 'Not equal' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '请重算', en: 'Recount' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '这不公平', en: 'Unfair' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '重算账', en: 'Recount' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '说清单', en: 'List items' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '给方案', en: 'Offer split' } }
    ],
    long_press_suppress: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '忍一下', en: 'Hold it' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '看账单', en: 'Read bill' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '先记账', en: 'Log it' } }
    ],
    swipe_left_play_cool: [
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '心里吐槽', en: 'Inner rant' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '笑着付', en: 'Pay smiling' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '阴阳一句', en: 'Dry jab' } }
    ]
  },
  photo_tag_unflattering: {
    double_tap_meme: [
      { scoreChoiceId: 'meme_reply', label: { 'zh-CN': '求打码', en: 'Blur me' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '反手丑图', en: 'Send bad pic' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '发个梗', en: 'Meme it' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '请删图', en: 'Delete it' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '私聊说', en: 'DM ask' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '换一张', en: 'Swap pic' } }
    ],
    swipe_up_escape: [
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '先隐身', en: 'Hide' } },
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '不点赞', en: 'No like' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '晚点说', en: 'Later' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '别发这张', en: 'Not this' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '请删掉', en: 'Delete it' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '别圈我', en: 'No tag' } }
    ],
    fast_fling_nope: [
      { scoreChoiceId: 'hard_exit', label: { 'zh-CN': '直接撤', en: 'Pull out' } },
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '不看了', en: 'Stop look' } },
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '删标签', en: 'Untag' } }
    ]
  },
  voice_message_wall: {
    timed_hesitation: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '先不点', en: 'Not now' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '开倍速', en: '2x it' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '请文字', en: 'Ask for text' } }
    ],
    swipe_up_escape: [
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '装没听', en: 'Miss audio' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '晚点听', en: 'Later' } },
      { scoreChoiceId: 'leave_on_read', label: { 'zh-CN': '先躲开', en: 'Step away' } }
    ],
    double_tap_meme: [
      { scoreChoiceId: 'meme_reply', label: { 'zh-CN': '耳朵累了', en: 'Ears tired' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '发个梗', en: 'Send meme' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '截图笑', en: 'Screenshot' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '发文字吧', en: 'Text please' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '说重点', en: 'Point first' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '别连发', en: 'No burst' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '转文字吗', en: 'Transcribe?' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '分条说', en: 'Split it' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '约个时间', en: 'Set time' } }
    ],
    long_press_suppress: [
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '先不点', en: 'Hold off' } },
      { scoreChoiceId: 'mute_chat', label: { 'zh-CN': '静音会儿', en: 'Mute a bit' } },
      { scoreChoiceId: 'cancel_cleanly', label: { 'zh-CN': '听一半', en: 'Skim it' } }
    ]
  },
  public_correction_pressure: {
    shake_spiral: [
      { scoreChoiceId: 'plot_twist', label: { 'zh-CN': '脸热了', en: 'Face hot' } },
      { scoreChoiceId: 'type_delete', label: { 'zh-CN': '脑内复盘', en: 'Mind replay' } },
      { scoreChoiceId: 'ghost_then_return', label: { 'zh-CN': '气笑了', en: 'Angry laugh' } }
    ],
    swipe_left_play_cool: [
      { scoreChoiceId: 'fake_busy', label: { 'zh-CN': '装镇定', en: 'Act calm' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '笑一下', en: 'Smile' } },
      { scoreChoiceId: 'screenshot_to_friend', label: { 'zh-CN': '记心里', en: 'Note inside' } }
    ],
    swipe_right_fix: [
      { scoreChoiceId: 'offer_solution', label: { 'zh-CN': '补一句', en: 'Add note' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '认个错', en: 'Own miss' } },
      { scoreChoiceId: 'organize_group', label: { 'zh-CN': '拉回正题', en: 'Back topic' } }
    ],
    swipe_down_confront: [
      { scoreChoiceId: 'direct_callout', label: { 'zh-CN': '我补充', en: 'I add' } },
      { scoreChoiceId: 'set_boundary', label: { 'zh-CN': '别打断', en: 'Do not cut' } },
      { scoreChoiceId: 'polite_venom', label: { 'zh-CN': '说完整', en: 'Let finish' } }
    ]
  }
};

export const compatibleSecondaryChoicesByEvent: Record<CoreEvent, string[]> = {
  swipe_up_escape: ['ghost_then_return', 'leave_on_read', 'fake_busy'],
  swipe_down_confront: ['direct_callout', 'set_boundary', 'polite_venom'],
  swipe_left_play_cool: ['polite_venom', 'screenshot_to_friend', 'fake_busy'],
  swipe_right_fix: ['offer_solution', 'set_boundary', 'organize_group', 'cancel_cleanly'],
  long_press_suppress: ['type_delete', 'mute_chat', 'cancel_cleanly'],
  double_tap_meme: ['meme_reply', 'polite_venom', 'screenshot_to_friend'],
  shake_spiral: ['plot_twist', 'type_delete', 'ghost_then_return'],
  fast_fling_nope: ['hard_exit', 'leave_on_read', 'fake_busy'],
  timed_hesitation: ['type_delete', 'ghost_then_return', 'mute_chat']
};

const actionKindByScoreChoiceId: Record<string, string> = {
  cancel_cleanly: 'clean_close',
  direct_callout: 'direct_reply',
  fake_busy: 'cover_exit',
  ghost_then_return: 'delay_return',
  hard_exit: 'hard_exit',
  leave_on_read: 'drop_thread',
  meme_reply: 'joke_reply',
  mute_chat: 'low_energy_hold',
  offer_solution: 'repair_plan',
  organize_group: 'structure_takeover',
  plot_twist: 'chaos_move',
  polite_venom: 'controlled_edge',
  screenshot_to_friend: 'private_rant',
  set_boundary: 'boundary_line',
  type_delete: 'internal_draft'
};

const scenePlayerCopy: Record<Locale, {
  progress: string;
  primaryKicker: string;
  secondaryKicker: string;
  secondaryTitle: string;
}> = {
  'zh-CN': {
    progress: '进度',
    primaryKicker: '社交现场',
    secondaryKicker: '下一步',
    secondaryTitle: '刚才这个反应之后，你会怎么处理？'
  },
  en: {
    progress: 'Scene',
    primaryKicker: 'Social scene',
    secondaryKicker: 'Follow-up',
    secondaryTitle: 'What would you do next?'
  }
};

export type SecondaryChoice = {
  id: string;
  scoreChoiceId: string;
  actionKind: string;
  label: string;
};

function fallbackChoicesFor(coreEvent: CoreEvent): SceneFollowUpChoice[] {
  return compatibleSecondaryChoicesByEvent[coreEvent].slice(0, 3).map((scoreChoiceId) => ({
    scoreChoiceId,
    label: { 'zh-CN': scoreChoiceId, en: scoreChoiceId }
  }));
}

export function sceneSecondaryChoicesFor(sceneId: string, coreEvent: CoreEvent, locale: Locale = 'en'): SecondaryChoice[] {
  const choices = sceneFollowUps[sceneId]?.[coreEvent] ?? fallbackChoicesFor(coreEvent);
  return choices.slice(0, 3).map((choice, index) => ({
    id: `${sceneId}:${coreEvent}:${index}`,
    scoreChoiceId: choice.scoreChoiceId,
    actionKind: actionKindByScoreChoiceId[choice.scoreChoiceId] ?? choice.scoreChoiceId,
    label: choice.label[locale]
  }));
}

function modifiersFromGesture(coreEvent: CoreEvent, telemetry: GestureTelemetry): DimensionDeltas[] {
  const modifiers: DimensionDeltas[] = [];

  if (coreEvent === 'timed_hesitation' || telemetry.dwellTimeMs > 4000) {
    modifiers.push({ avoidance: 3, emotionalVolatility: 2, impulsiveness: -2 });
  }
  if (telemetry.velocity > 1.25) {
    modifiers.push({ impulsiveness: Math.min(5, Math.max(1, Math.round(telemetry.velocity * 2))) });
  } else if (telemetry.distance > 0 && telemetry.velocity < 0.18) {
    modifiers.push({ avoidance: 2 });
  }
  if (telemetry.reversalCount > 0) {
    modifiers.push({ emotionalVolatility: Math.min(4, telemetry.reversalCount) });
  }

  return modifiers;
}

const FULL_SCENE_COUNT = 12;

function hashSeed(seed: string) {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function randomFromSeed(seed: string) {
  let value = hashSeed(seed);
  return () => {
    value += 0x6d2b79f5;
    let next = value;
    next = Math.imul(next ^ (next >>> 15), next | 1);
    next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffledScenes(input: Scene[], seed: string) {
  const random = randomFromSeed(seed);
  const next = [...input];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }
  return next;
}

export function createSceneRun(seed: string, count = FULL_SCENE_COUNT) {
  const buckets = new Map<Scene['category'], Scene[]>();
  for (const scene of shuffledScenes(scenes, `${seed}:pool`)) {
    const bucket = buckets.get(scene.category) ?? [];
    bucket.push(scene);
    buckets.set(scene.category, bucket);
  }

  const categories = shuffledScenes(
    [...buckets.keys()].map((category) => buckets.get(category)?.[0]).filter((scene): scene is Scene => Boolean(scene)),
    `${seed}:categories`
  ).map((scene) => scene.category);

  const selected: Scene[] = [];
  while (selected.length < count) {
    let added = false;
    for (const category of categories) {
      const scene = buckets.get(category)?.shift();
      if (!scene) continue;
      selected.push(scene);
      added = true;
      if (selected.length === count) break;
    }
    if (!added) break;
  }

  return shuffledScenes(selected, `${seed}:run`);
}

function createRunSeed() {
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    const values = new Uint32Array(2);
    crypto.getRandomValues(values);
    return `${values[0].toString(36)}-${values[1].toString(36)}`;
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function scenesForCodes(sceneCodes: string[]) {
  const selected = sceneCodes
    .map((code) => scenes[Number(code) - 1])
    .filter((scene): scene is typeof scenes[number] => Boolean(scene));
  return selected.length ? selected : scenes.slice(0, 3);
}

export function ScenePlayerPage({ mode = 'full' }: { mode?: 'full' | 'challenge' }) {
  const { locale } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const scenePlayerRef = useRef<HTMLElement | null>(null);
  const sceneRunSeed = useMemo(() => createRunSeed(), []);
  const challengePayload = useMemo(() => parseChallengePayload(location.search), [location.search]);
  const activeSceneCodes = useMemo(
    () => (mode === 'challenge' ? challengePayload.sceneCodes : []),
    [challengePayload.sceneCodes, mode]
  );
  const activeScenes = useMemo(
    () => (mode === 'challenge' ? scenesForCodes(activeSceneCodes) : createSceneRun(sceneRunSeed)),
    [activeSceneCodes, mode, sceneRunSeed]
  );
  const [index, setIndex] = useState(0);
  const [events, setEvents] = useState<ScoreEventInput[]>([]);
  const [pendingAction, setPendingAction] = useState<{
    coreEvent: CoreEvent;
    telemetry: GestureTelemetry;
    choices: SecondaryChoice[];
  } | null>(null);
  const scene = activeScenes[index];
  const copy = scenePlayerCopy[locale];
  const sceneCopy = scene.copy[locale];
  const copyDensity =
    sceneCopy.length > (locale === 'en' ? 72 : 34)
      ? 'long'
      : sceneCopy.length > (locale === 'en' ? 48 : 24)
        ? 'medium'
        : 'short';

  useEffect(() => {
    let frame = 0;

    function updateViewportHeight() {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const sceneCardHeight = viewportHeight < 700 ? 112 : viewportHeight < 820 ? 136 : 160;
        scenePlayerRef.current?.style.setProperty('--scene-vh', `${Math.round(viewportHeight)}px`);
        scenePlayerRef.current?.style.setProperty('--scene-card-h', `${sceneCardHeight}px`);
      });
    }

    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);
    window.visualViewport?.addEventListener('resize', updateViewportHeight);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
      window.visualViewport?.removeEventListener('resize', updateViewportHeight);
    };
  }, []);

  function commitAction(coreEvent: CoreEvent, telemetry: GestureTelemetry, secondaryChoiceId?: string) {
    const nextEvents = [
      ...events,
      {
        sceneId: scene.sceneId,
        category: scene.category,
        gestureId: coreEvent,
        coreEvent,
        secondaryChoiceId,
        modifiers: modifiersFromGesture(coreEvent, telemetry)
      }
    ];

    if (index === activeScenes.length - 1) {
      const scoreResult = scoreEvents(nextEvents);
      const match = matchPersonalityType(scoreResult.scores, nextEvents);
      sessionStorage.setItem('vibeArcade.latestResult', JSON.stringify({ scores: scoreResult.scores, match, events: nextEvents }));
      if (mode === 'challenge') {
        const sceneCodes = activeSceneCodes.length
          ? activeSceneCodes
          : activeScenes.map((item) => String(scenes.indexOf(item) + 1).padStart(2, '0'));
        const playerTypeId = match.primary.typeId;
        sessionStorage.setItem(
          'vibeArcade.challengeComparison',
          JSON.stringify({
            friendTypeId: challengePayload.typeId,
            playerTypeId,
            sceneCodes
          })
        );
        navigate(`/compare?friend=${encodeURIComponent(challengePayload.typeId)}&you=${encodeURIComponent(playerTypeId)}&c=${sceneCodes.join('.')}`);
        return;
      }
      navigate('/result');
      return;
    }

    setEvents(nextEvents);
    setPendingAction(null);
    setIndex(index + 1);
  }

  function handleSelect(coreEvent: CoreEvent, telemetry: GestureTelemetry) {
    const choices = sceneSecondaryChoicesFor(scene.sceneId, coreEvent, locale);
    if (choices.length > 0) {
      setPendingAction({ coreEvent, telemetry, choices });
      return;
    }
    commitAction(coreEvent, telemetry);
  }

  return (
    <main className="scene-player" data-scene-category={scene.category} ref={scenePlayerRef}>
      <section className="scene-shell">
        <header className="scene-topbar">
          <span>
            {copy.progress} {index + 1} / {activeScenes.length}
          </span>
        </header>

        <article className="scene-card" data-copy-density={copyDensity}>
          <p>{copy.primaryKicker}</p>
          <h1>{sceneCopy}</h1>
        </article>

        <GestureSurface
          activeEvent={pendingAction?.coreEvent ?? null}
          disabled={Boolean(pendingAction)}
          locale={locale}
          supportedEvents={scene.supportedEvents}
          onSelect={handleSelect}
        />

        {pendingAction ? (
          <section className="secondary-sheet" aria-label={copy.secondaryTitle}>
            <p>{copy.secondaryKicker}</p>
            <h2>{copy.secondaryTitle}</h2>
            <div className="secondary-choice-grid">
              {pendingAction.choices.map((choiceId) => (
                <button
                  className="secondary-choice"
                  key={choiceId.id}
                  type="button"
                  onClick={() => commitAction(pendingAction.coreEvent, pendingAction.telemetry, choiceId.scoreChoiceId)}
                >
                  {choiceId.label}
                </button>
              ))}
            </div>
          </section>
        ) : null}
      </section>
    </main>
  );
}
